import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../shared/types/common";
import { useNavigate } from "react-router-dom";
import { useForm } from "antd/lib/form/Form";
import { TABLE_ROWS_COUNT } from "../../shared/constants";
import {
  deleteAccount,
  getAccountsList,
} from "../../core/store/reducers/accounts.slice";
import { Space, Button, Col, Row, Modal, Drawer } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  AccountBookOutlined,
} from "@ant-design/icons";
import { accessAllowed, showNotification } from "../../shared/utils/utils";
import { BiFilterAlt } from "react-icons/bi";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import DataTableComponent from "../../shared/components/data-table/data-table.component";
import CreateEditAccountsComponent from "./components/create-edit-accounts.component";
import ActivityLogComponent from "../../shared/components/activity-log/activity-log.component";
import { allPermissions } from "../../shared/constants/constants";
import { ActiveFilterChipsBar } from "../../shared/components/active-filter-chips/active-filter-chips.component";
import { isFilterValueActive } from "../../shared/utils/active-filters";

const ACCOUNTS_FILTER_LABELS: Record<string, string> = {
  accountName: "Client",
};

const ACCOUNTS_FILTER_CLEAR: Record<string, any> = {
  accountName: undefined,
};

const ManageAccountsPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = useForm();
  const { loadingAccount, accountList, pagination } = useSelector(
    (state: RootState) => state.account
  );
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [accountIdToDelete, setAccountIdToDelete] = useState("");
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [accountListData, setAccountListData] = useState([] as any);
  const [createEditOpen, setCreateEditOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null as any);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filters, setFilters] = useState({} as any);
  const [sorter, setSorter] = useState({ sort_by: "", sort_type: "" });
  const [openLogs, setOpenLogs] = useState(false);
  useEffect(() => {
    loadList();
  }, [filters, sorter]);

  const loadList = (page: any = null) => {
    const data = {
      page: page ? page : pagination?.page ? pagination?.page : 1,
      limit: pagination?.limit || TABLE_ROWS_COUNT,
      ...sorter,
    };
    let filter = {} as any;
    Object.keys(filters)?.map((val: any) => {
      if (filters[val]) filter[val] = filters[val];
    });
    dispatch(getAccountsList({ params: data, body: filter }));
  };

  const createEntityListner = () => {
    setCreateEditOpen(true);
    setSelectedEntity(null);
  };
  const editEntityListner = (entity: any) => {
    setCreateEditOpen(true);
    setSelectedEntity(entity);
  };

  useEffect(() => {
    setAccountListData(
      accountList?.map((obj: any, index: number) => {
        return { key: index, ...obj };
      })
    );
  }, [accountList]);
  const onFilterApply = (values: any) => {
    setFilters(values); //filters
  };

  const activeFilterEntries = useMemo(() => {
    return Object.keys(filters)
      .filter((k) => isFilterValueActive(filters[k]))
      .map((key) => ({
        key,
        fieldLabel: ACCOUNTS_FILTER_LABELS[key] || key,
        valueText: String(filters[key] ?? "").trim(),
      }));
  }, [filters]);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    form.resetFields();
  }, [form]);

  const removeFilter = useCallback(
    (key: string) => {
      setFilters((prev: any) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const cleared =
        key in ACCOUNTS_FILTER_CLEAR ? ACCOUNTS_FILTER_CLEAR[key] : undefined;
      form.setFieldsValue({ [key]: cleared });
    },
    [form]
  );

  const onPageChange = (pageNumber: number) => {
    // loadList((pageNumber - 1) * pagination?.limit);
    loadList(pageNumber);
  };
  const hasAnyAccountAccess =
  accessAllowed(myPermissions, allPermissions.UPDATE_ACCOUNT) ||
  accessAllowed(myPermissions, allPermissions.DELETE_ACCOUNT) ||
  accessAllowed(myPermissions, allPermissions.ACCOUNT_ACTIVITY_LOGS);
  const columns = [
    {
      title: "Client Name",
      dataIndex: "accountName",
      sorter: false,
      key: "accountName",
    },
    {
      title: "Client Type",
      dataIndex: "accountCode",
      sorter: false,
      key: "accountCode",
    },
    {
      title: "Client Legal Entity",
      dataIndex: "clientName",
      sorter: false,
      key: "clientName",
    },
    {
      title: "Entity",
      dataIndex: "entityName",
      sorter: false,
      key: "entity",
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: false,
      key: "status",
    },
    ...(hasAnyAccountAccess
      ? [
          {
            title: "Actions",
            key: "actions",
            fixed: "right",
            render: (text: any, record: any) => (
              <Space>
                {accessAllowed(myPermissions, allPermissions.UPDATE_ACCOUNT) && (
                  <Button
                    type="link"
                    onClick={() => editEntityListner(record)}
                    icon={<EditOutlined />}
                  />
                )}
                {accessAllowed(myPermissions, allPermissions.DELETE_ACCOUNT) && (
                  <Button
                    type="link"
                    onClick={() => handleDeleteModal(record?.accountId)}
                    icon={<DeleteOutlined style={{color:"#FF0000"}} />}
                  />
                )}
                {accessAllowed(myPermissions, allPermissions.ACCOUNT_ACTIVITY_LOGS) && (
                  <Button
                    type="link"
                    onClick={() => handleOpenLogs(record)}
                    icon={<EyeOutlined />}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ];

  const handleDelete = (accountId: string) => {
    dispatch(deleteAccount(accountId)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Client Deleted Successfully", "success");
        loadList();
        setDeleteModalVisible(false); // Close the modal after successful deletion
      }
    });
  };

  const handleOpenLogs = (record: any) => {
    setOpenLogs(true);
    setAccountIdToDelete(record?.accountId);
    setSelectedEntity(record);
  };

  const onCloseLogs = () => {
    setOpenLogs(false);
    setAccountIdToDelete("");
  };

  const handleDeleteModal = (accountId: string) => {
    setDeleteModalVisible(true);
    setAccountIdToDelete(accountId);
  };

  const onDeleteConfirm = () => {
    handleDelete(accountIdToDelete);
  };

  const onDeleteCancel = () => {
    setDeleteModalVisible(false);
    setAccountIdToDelete("");
  };

  const onChangeHandler = (_pagination: any, _filters: any, _sorter: any) => {
    if (_sorter.order === undefined) {
      setSorter({ sort_by: "", sort_type: "" });
    } else {
      let order = "";
      let columnKey = "";
      if (_sorter.order === "ascend") order = "asc";
      else order = "desc";

      if (_sorter.columnKey === "enabled") {
        columnKey = "status";
      } else if (_sorter.columnKey === "displayName") {
        columnKey = "name";
      } else {
        columnKey = _sorter.columnKey;
      }
      setSorter({ sort_by: columnKey, sort_type: order });
    }
  };

  const createEditSubmit = () => {
    setCreateEditOpen(false);
    loadList();
  };

  useEffect(() => {
    loadList();
  }, []);
  const onValuesChange = (value: any, _allValues: any) => {};
  const formProps = {
    form: form,
    name: "manage-client-filters",
    enableEnterSubmit: true,
    formElements: [
      {
        name: "accountName",
        label: "",
        placeholder: "Client Search",
        type: "Input",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 4 },
      },
    ],
    initialValues: {
      name: "",
    },
    extraButtons: [
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpanResponsive: { xs: 12, sm: 8, md: 6, lg: 3 },
        loading: false,
        submitButtonText: "Apply",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFilterApply,
    onFinishFailed: null,
    onValuesChange: onValuesChange,
  };

  const headerButtons = (
    <Space size="middle" className="app-page-actions">
      {accessAllowed(myPermissions, allPermissions.CREATE_ACCOUNT) && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={createEntityListner}
        >
          <span className="font-size-14">
            New {!screenType?.isPortrait && "Client"}
          </span>
        </Button>
      )}
      <Button
        shape="circle"
        type="primary"
        icon={<BiFilterAlt />}
        onClick={() => setFilterVisible(!filterVisible)}
        title={filterVisible ? "Hide filters" : "Show filters"}
      />
    </Space>
  );

  return (
    <div className="app-page">
      <div className="app-page-header">
        <Row align="middle" justify="space-between" wrap>
          <Col flex="1" style={{ minWidth: 0 }}>
            <div className="app-page-title-wrap">
              <div className="app-page-icon">
                <AccountBookOutlined />
              </div>
              <h1 className="app-page-title">Manage Client</h1>
            </div>
            <p className="app-page-subtitle">
              View and manage clients and accounts
            </p>
          </Col>
          <Col>{headerButtons}</Col>
        </Row>
      </div>
      {filterVisible && (
        <div className="app-filter-bar">
          <div className="app-filter-title">
            <BiFilterAlt style={{ fontSize: 14 }} />
            Filters
          </div>
          <div className="table-filter-form">
            <GenericFormComponent {...formProps} />
          </div>
        </div>
      )}

      <ActiveFilterChipsBar
        show={!filterVisible}
        entries={activeFilterEntries}
        onRemove={removeFilter}
        onClearAll={clearAllFilters}
      />

      <div className="app-table-card">
        <DataTableComponent
        pagination={true}
        loading={loadingAccount}
        dataSource={accountListData}
        columns={columns}
        onChange={onChangeHandler}
        defaultPage={0}
        currentPage={pagination.page}
        totalRows={pagination?.total}
        onPageChange={onPageChange}
      />
      </div>
      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={onDeleteConfirm}
        onCancel={onDeleteCancel}
      >
        <p>Are you sure you want to delete this client?</p>
      </Modal>
      {createEditOpen && (
        <CreateEditAccountsComponent
          onClose={() => setCreateEditOpen(false)}
          visible={createEditOpen}
          selectedEntity={selectedEntity}
          onSubmit={createEditSubmit}
        />
      )}
      {openLogs && (
        <Drawer open={openLogs} onClose={onCloseLogs} title="Log Details">
          <ActivityLogComponent
            id={selectedEntity?.accountId || accountIdToDelete}
            record={selectedEntity}
            module="Client"
          />
        </Drawer>
      )}
    </div>
  );
};
export default ManageAccountsPage;
